import { useState, useEffect } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../../firebaseConfig';
import { Bar, Doughnut } from 'react-chartjs-2';
import { MdNightsStay, MdDateRange, MdUpcoming } from 'react-icons/md';
import { Chart as ChartJS, BarElement, CategoryScale, LinearScale, ArcElement, Tooltip, Legend } from 'chart.js';
import './index.css';

ChartJS.register(BarElement, CategoryScale, LinearScale, ArcElement, Tooltip, Legend);

/**
 * Dashboard component displaying booking analytics
 * Shows total nights booked, remaining nights, and upcoming bookings with charts
 * @param {string} ownerId - The ID of the owner
 * @returns {JSX.Element} Dashboard with analytics charts and stats
 */
const Dashboard = ({ ownerId }) => {
  const [totalNights, setTotalNights] = useState(0);
  const [remainingNights, setRemainingNights] = useState(15);
  const [upcomingBookings, setUpcomingBookings] = useState(0);
  const [bookingData, setBookingData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchActiveBookings = async () => {
      if (!ownerId) {
        setError('Owner not authenticated');
        setLoading(false);
        return;
      }

      try {
        const bookingsCollection = collection(db, 'bookings');
        const activeBookingsQuery = query(
          bookingsCollection,
          where('status', '==', 'active'),
          where('ownerId', '==', ownerId)
        );
        const bookingSnapshot = await getDocs(activeBookingsQuery);
        const bookings = bookingSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));

        let activeNights = 0;
        let activeBookingCount = 0;
        const monthRanges = {};
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const dateRanges = ['1-5', '6-10', '11-15', '16-20', '21-25', '26-31'];

        months.forEach(month => {
          monthRanges[month] = {
            '1-5': 0,
            '6-10': 0,
            '11-15': 0,
            '16-20': 0,
            '21-25': 0,
            '26-31': 0,
          };
        });

        bookings.forEach(booking => {
          const startDate = new Date(booking.startDate);
          const endDate = new Date(booking.endDate);
          const nights = Math.floor((endDate - startDate) / (1000 * 60 * 60 * 24));

          if (nights <= 0) return;

          activeNights += nights;
          activeBookingCount += 1;

          const month = startDate.toLocaleString('default', { month: 'short' });
          const startDay = startDate.getDate();

          let range;
          if (startDay <= 5) range = '1-5';
          else if (startDay <= 10) range = '6-10';
          else if (startDay <= 15) range = '11-15';
          else if (startDay <= 20) range = '16-20';
          else if (startDay <= 25) range = '21-25';
          else range = '26-31';

          monthRanges[month][range] += nights;
        });

        const datasets = months.map(month => ({
          label: month,
          data: dateRanges.map(range => monthRanges[month][range]),
          backgroundColor: `#${Math.floor(Math.random() * 16777215).toString(16)}`,
        }));

        setTotalNights(activeNights);
        setRemainingNights(15 - activeNights);
        setUpcomingBookings(activeBookingCount);
        setBookingData({ dateRanges, datasets });
        setLoading(false);
      } catch (err) {
        setError('Failed to fetch bookings');
        setLoading(false);
      }
    };

    fetchActiveBookings();
  }, [ownerId]);

  const barData = {
    labels: bookingData.dateRanges || ['1-5', '6-10', '11-15', '16-20', '21-25', '26-31'],
    datasets: bookingData.datasets || [],
  };

  const doughnutData = {
    labels: ['Total Nights Booked', 'Remaining Nights'],
    datasets: [{
      data: [totalNights, remainingNights],
      backgroundColor: ['#3b82f6', '#60a5fa'],
    }],
  };

  const chartOptions = {
    indexAxis: 'x',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Months & Dates',
        },
      },
      y: {
        title: {
          display: true,
          text: 'Number of Nights Booked',
        },
        min: 0,
        max: 16,
        ticks: {
          stepSize: 2,
          callback: function (value) {
            return value % 2 === 0 ? value : null;
          },
        },
      },
    },
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
      },
    },
  };

  if (loading) return (
    <div className="spinner-container">
      <div className="spinner"></div>
    </div>
  );
  if (error) return <div>{error}</div>;

  return (
    <div className="dashboard">
      <h2>Owner Dashboard</h2>
      <div className="stats-cards">
        <div className="card-dash">
          <h6>
            <span className="icon-circle">
              <MdNightsStay size={20} />
            </span>
            Total Nights Booked
          </h6>
          <p>{totalNights}</p>
        </div>
        <div className="card-dash">
          <h6>
            <span className="icon-circle">
              <MdDateRange size={20} />
            </span>
            Remaining Nights
          </h6>
          <p>{remainingNights}</p>
        </div>
        <div className="card-dash">
          <h6>
            <span className="icon-circle">
              <MdUpcoming size={20} />
            </span>
            Upcoming Bookings
          </h6>
          <p>{upcomingBookings}</p>
        </div>
      </div>
      <div className="charts-section">
        <div className="chart-card-dash">
          <h6>Bookings Analytics</h6>
          <Bar data={barData} options={chartOptions} />
        </div>
        <div className="chart-card">
          <h6>Booking Overview</h6>
          <Doughnut data={doughnutData} options={doughnutOptions} />
        </div>
      </div>
    </div>
  );
};

export default Dashboard;